import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileStack, FileText, Search, Filter, Eye, Download, Briefcase, Users, Clock, HardDrive } from 'lucide-react';
import { documentService } from '../services/api';
import useNotification from '../hooks/useNotification';
import Notification from '../components/common/Notification';
import DocumentTemplates from './DocumentTemplates';

const TABS = [
  { id: 'documents', label: 'Documents', icon: FileStack },
  { id: 'templates', label: 'Templates', icon: FileText },
];

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMimeIcon(mimeType) {
  if (!mimeType) return 'file';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('word') || mimeType.includes('docx')) return 'doc';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'xls';
  if (mimeType.includes('image')) return 'img';
  return 'file';
}

function MimeTypeBadge({ mimeType }) {
  const type = getMimeIcon(mimeType);
  const config = {
    pdf: { label: 'PDF', className: 'bg-red-50 text-red-700' },
    doc: { label: 'DOC', className: 'bg-blue-50 text-blue-700' },
    xls: { label: 'XLS', className: 'bg-green-50 text-green-700' },
    img: { label: 'IMG', className: 'bg-purple-50 text-purple-700' },
    file: { label: 'FILE', className: 'bg-gray-50 text-gray-700' },
  };
  const { label, className } = config[type] || config.file;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

const AllDocumentsTab = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [documentType, setDocumentType] = useState('');
  const { notification, showError, hideNotification } = useNotification();

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: 50,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (search) params.search = search;
      if (documentType) params.document_type = documentType;
      const res = await documentService.getDocuments(params);
      setDocuments(res.data.documents || []);
      setTotalPages(res.data.total_pages || 1);
      setTotalCount(res.data.total_count || 0);
    } catch {
      showError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortOrder, documentType, showError]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [search, documentType]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIndicator = ({ column }) => {
    if (sortBy !== column) return null;
    return <span className="ml-1 text-xs">{sortOrder === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  return (
    <div>
      <Notification {...notification} onClose={hideNotification} />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
          >
            <option value="">All Types</option>
            <option value="generated">Generated</option>
            <option value="uploaded">Uploaded</option>
            <option value="scanned">Scanned</option>
            <option value="imported">Imported</option>
          </select>
        </div>
        <div className="text-sm text-gray-500 self-center">
          {totalCount} document{totalCount !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Documents table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <FileStack className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No documents found</h3>
          <p className="text-sm text-gray-500">
            Documents uploaded to cases will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('name')}
                  >
                    Name <SortIndicator column="name" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Case</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Client</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Type</th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('date')}
                  >
                    Date <SortIndicator column="date" />
                  </th>
                  <th
                    className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:text-gray-900"
                    onClick={() => handleSort('size')}
                  >
                    Size <SortIndicator column="size" />
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Uploaded By</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr
                    key={doc.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <MimeTypeBadge mimeType={doc.mime_type} />
                        <span className="font-medium text-gray-900 truncate max-w-xs" title={doc.original_file_name}>
                          {doc.original_file_name}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {doc.case_id ? (
                        <Link
                          to={`/cases/${doc.case_id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          title={doc.case_title || ''}
                        >
                          <div className="flex items-center gap-1">
                            <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate max-w-[150px]">{doc.case_number || doc.case_title || 'View Case'}</span>
                          </div>
                        </Link>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {doc.client_name ? (
                        <div className="flex items-center gap-1 text-sm text-gray-700">
                          <Users className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                          <span className="truncate max-w-[120px]">{doc.client_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">{doc.document_type || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Clock className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        {formatDate(doc.created_at)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <HardDrive className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                        {formatFileSize(doc.file_size)}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">{doc.uploaded_by_name || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        {doc.case_id && (
                          <button
                            onClick={() => navigate(`/cases/${doc.case_id}/documents`)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                            title="View in case"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={async () => {
                            try {
                              const dlRes = await documentService.getDownloadUrl(doc.id, true);
                              if (dlRes?.data?.download_url) {
                                window.open(dlRes.data.download_url, '_blank');
                              }
                            } catch {
                              // Fallback: navigate to case documents
                              if (doc.case_id) navigate(`/cases/${doc.case_id}/documents`);
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-green-600 rounded hover:bg-green-50 transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DocumentsAndTemplates = () => {
  const [activeTab, setActiveTab] = useState('documents');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileStack className="h-6 w-6 text-blue-600" />
          Documents & Templates
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Browse all documents across cases and manage reusable document templates
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'documents' && <AllDocumentsTab />}
      {activeTab === 'templates' && <DocumentTemplates />}
    </div>
  );
};

export default DocumentsAndTemplates;
